import React, { useMemo, useState } from "react";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";
import { SlidersHorizontal, X } from "lucide-react";
import { CATEGORIES, MOTORCYCLES, PRICE_MIN, PRICE_MAX } from "../data/motorcycles";
import { formatIDR } from "../lib/whatsapp";
import MotorCard from "./MotorCard";

// pembulatan slider ke jutaan
const STEP = 500_000;
const roundUp = (v) => Math.ceil(v / STEP) * STEP;
const roundDown = (v) => Math.floor(v / STEP) * STEP;
const MIN = roundDown(PRICE_MIN);
const MAX = roundUp(PRICE_MAX);

export default function CatalogGrid() {
  const [cat, setCat] = useState("Semua");
  const [maxPrice, setMaxPrice] = useState(MAX);
  const [filterOpen, setFilterOpen] = useState(false);

  const items = useMemo(() => {
    return MOTORCYCLES
      .filter((m) => (cat === "Semua" || m.category === cat) && m.price_idr <= maxPrice)
      .sort((a, b) => a.order - b.order);
  }, [cat, maxPrice]);

  const priceActive = maxPrice < MAX;

  return (
    <section id="katalog" className="relative py-8 md:py-12 bg-white" data-testid="section-catalog">
      <div className="y-container">
        {/* Category Tabs */}
        <LayoutGroup>
          <div className="mb-4 -mx-4 px-4">
            <div className="flex gap-2 sm:gap-3 overflow-x-auto no-scrollbar snap-x pb-1" data-testid="catalog-tabs">
              {CATEGORIES.map((c) => {
                const active = c.key === cat;
                return (
                  <button
                    key={c.key}
                    onClick={() => setCat(c.key)}
                    data-testid={`catalog-tab-${c.key}`}
                    className={`relative snap-start shrink-0 flex flex-col items-center gap-1 px-4 sm:px-5 py-3 sm:py-3.5 rounded-2xl border transition-all min-w-[74px] sm:min-w-[92px] ${
                      active
                        ? "bg-[#002d62] border-[#002d62] text-white shadow-md"
                        : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"
                    }`}
                  >
                    {c.icon ? (
                      <img
                        src={c.icon}
                        alt=""
                        className={`w-8 h-8 sm:w-10 sm:h-10 object-contain transition-all ${
                          active ? "brightness-0 invert" : ""
                        }`}
                      />
                    ) : (
                      <span className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-lg font-black ${active ? "bg-white/20" : "bg-slate-100"}`}>
                        ★
                      </span>
                    )}
                    <span className="text-[11px] sm:text-xs font-semibold">{c.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </LayoutGroup>

        {/* Price Filter */}
        <div className="mb-5 flex items-center justify-between gap-3 flex-wrap">
          <button
            onClick={() => setFilterOpen((v) => !v)}
            data-testid="filter-toggle"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              priceActive || filterOpen
                ? "border-[#002d62] bg-[#002d62] text-white"
                : "border-slate-200 text-slate-700 hover:border-slate-300"
            }`}
          >
            <SlidersHorizontal className="w-3.5 h-3.5" />
            Filter Budget {priceActive && `≤ ${formatIDR(maxPrice)}`}
          </button>
          <span className="text-[11px] sm:text-xs text-slate-500" data-testid="filter-count">
            Menampilkan <span className="font-bold text-slate-900">{items.length}</span> dari {MOTORCYCLES.length} motor
          </span>
        </div>

        <AnimatePresence>
          {filterOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden mb-5"
              data-testid="filter-panel"
            >
              <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-slate-700">
                    Budget maksimal: <span className="text-[#002d62]">{formatIDR(maxPrice)}</span>
                  </span>
                  {priceActive && (
                    <button
                      onClick={() => setMaxPrice(MAX)}
                      data-testid="filter-reset"
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-slate-800"
                    >
                      <X className="w-3 h-3" /> Reset
                    </button>
                  )}
                </div>
                <input
                  type="range"
                  min={MIN}
                  max={MAX}
                  step={STEP}
                  value={maxPrice}
                  data-testid="filter-price-slider"
                  onChange={(e) => setMaxPrice(Number(e.target.value))}
                  className="w-full accent-[#e60012]"
                />
                <div className="mt-1 flex justify-between text-[10px] text-slate-400 font-medium">
                  <span>{formatIDR(MIN)}</span>
                  <span>{formatIDR(MAX)}</span>
                </div>

                {/* Quick presets */}
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {[20_000_000, 25_000_000, 30_000_000, 40_000_000, 50_000_000, 80_000_000].map((v) => (
                    <button
                      key={v}
                      onClick={() => setMaxPrice(v)}
                      data-testid={`filter-preset-${v}`}
                      className={`px-2.5 py-1 rounded-full text-[10px] font-semibold border transition ${
                        maxPrice === v
                          ? "bg-[#002d62] text-white border-[#002d62]"
                          : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      ≤ {formatIDR(v).replace("Rp", "Rp ")}
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Grid */}
        <AnimatePresence mode="popLayout">
          {items.length === 0 ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="py-16 text-center text-sm text-slate-500"
              data-testid="catalog-empty"
            >
              Tidak ada motor sesuai filter. Coba naikkan budget atau ganti kategori.
            </motion.div>
          ) : (
            <motion.div
              key={cat + maxPrice}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-5"
              data-testid="catalog-grid"
            >
              {items.map((m, i) => (
                <MotorCard key={m.slug} motor={m} index={i} />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
